import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { newsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Newspaper, 
  Search, 
  Plus,
  Megaphone,
  FileText,
  Calendar,
  Award,
  Pin,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const CompanyNews = () => {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'announcement',
    priority: 'normal',
    is_pinned: false
  });

  const isHR = user?.role === 'supervisor' || user?.role === 'executive';

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await newsAPI.getAll();
      setNews(response.data);
    } catch (error) {
      console.error('News fetch error:', error);
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Please fill in title and content');
      return;
    }

    try {
      await newsAPI.create(formData);
      toast.success('News article published!');
      setShowDialog(false);
      setFormData({
        title: '',
        content: '',
        category: 'announcement',
        priority: 'normal',
        is_pinned: false
      });
      fetchNews();
    } catch (error) {
      console.error('Create news error:', error);
      toast.error('Failed to publish news');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'announcement': return Megaphone;
      case 'policy': return FileText;
      case 'event': return Calendar;
      case 'achievement': return Award;
      default: return Newspaper;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'announcement': return 'bg-blue-100 text-blue-700';
      case 'policy': return 'bg-violet-100 text-violet-700';
      case 'event': return 'bg-emerald-100 text-emerald-700';
      case 'achievement': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'normal': return 'bg-slate-100 text-slate-600';
      case 'low': return 'bg-slate-50 text-slate-500';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredNews = news.filter(item =>
    item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.content?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort: pinned first, then by date
  const sortedNews = [...filteredNews].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return new Date(b.published_at) - new Date(a.published_at);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading news...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-news-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white border-none">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                <Newspaper className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Company News
                </h2>
                <p className="text-blue-100">
                  Stay updated with the latest announcements
                </p>
              </div>
            </div>
            {isHR && (
              <Button 
                onClick={() => setShowDialog(true)}
                className="bg-white text-indigo-600 hover:bg-indigo-50"
                data-testid="new-news-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Publish News
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Articles</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {news.length}
                </p>
              </div>
              <Newspaper className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Announcements</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {news.filter(n => n.category === 'announcement').length}
                </p>
              </div>
              <Megaphone className="w-10 h-10 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Events</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {news.filter(n => n.category === 'event').length}
                </p>
              </div>
              <Calendar className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Pinned</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {news.filter(n => n.is_pinned).length}
                </p>
              </div>
              <Pin className="w-10 h-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search news..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white"
          data-testid="news-search"
        />
      </div>

      {/* News List */}
      <div className="space-y-4">
        {sortedNews.map((item) => {
          const Icon = getCategoryIcon(item.category);
          return (
            <Card 
              key={item.id} 
              className={`card-hover ${item.is_pinned ? 'border-indigo-200 bg-indigo-50/30' : ''}`}
              data-testid={`news-item-${item.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getCategoryColor(item.category).replace('text-', 'bg-').split(' ')[0]}`}>
                    <Icon className={`w-6 h-6 ${getCategoryColor(item.category).split(' ')[1]}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {item.title}
                        </h3>
                        {item.is_pinned && (
                          <Pin className="w-4 h-4 text-indigo-600" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getCategoryColor(item.category)}>
                          {item.category?.charAt(0).toUpperCase() + item.category?.slice(1)}
                        </Badge>
                        {item.priority !== 'normal' && (
                          <Badge className={getPriorityColor(item.priority)}>
                            {item.priority?.charAt(0).toUpperCase() + item.priority?.slice(1)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-slate-600 mb-4 line-clamp-3">
                      {item.content}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(item.published_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {sortedNews.length === 0 && (
        <div className="text-center py-12">
          <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No News Found</h3>
          <p className="text-slate-500">Try adjusting your search or check back later</p>
        </div>
      )}

      {/* New News Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Publish Company News</DialogTitle>
            <DialogDescription>Create a new announcement for all employees</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                placeholder="Enter news title..."
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="announcement">Announcement</SelectItem>
                    <SelectItem value="policy">Policy Update</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="achievement">Achievement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={formData.priority}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Content *</Label>
              <Textarea
                placeholder="Write your news content here..."
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={6}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="pinned"
                checked={formData.is_pinned}
                onChange={(e) => setFormData(prev => ({ ...prev, is_pinned: e.target.checked }))}
                className="rounded border-slate-300"
              />
              <Label htmlFor="pinned" className="cursor-pointer">Pin this article to top</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700">
              Publish News
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyNews;
